import {
  useBidOrchestratorStream,
  useBidOrchestratorStreamContext,
  UseBidOrchestratorStreamResult,
} from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { QueuedBidOrchestrator } from "lib/providers/bid_orchestrator/types";
import { routeSegments } from "lib/routes";
import { BidOrchestratorPhase, BidReportType } from "lib/types/graphQLEnums";

/**
 * What the CTA card for one report on one bid should currently look like, plus
 * where clicking it should go. Lets consumers render off `state` and call
 * `navigate(navigateTo)` without re-deriving routing themselves.
 *
 *  - `idle`       → nothing in flight; CTA shows "Generate"
 *  - `processing` → user has committed but no content yet (uploads settling,
 *                   workflow dispatched but no `*_started` event). Click → loading page.
 *  - `streaming`  → report is actively streaming. Click → live report page.
 *  - `complete`   → report finished and persisted. Click → report page.
 *
 * Failure collapses to `idle` so the CTA returns to "Generate" (the provider
 * raises its own failure toast).
 */
export type ReportCTAState =
  | { state: "idle"; navigateTo?: never }
  | { state: "processing"; navigateTo: string }
  | { state: "streaming"; navigateTo: string }
  | { state: "complete"; navigateTo: string };

/**
 * Everything the BidContent header derives from the in-flight orchestrator run:
 * the CTA state for each report type plus whether the bid title is still being
 * generated. Bundled into one hook so BidContent reads the orchestrator stream
 * once rather than calling `useBidOrchestratorStream` three times.
 */
export type BidOrchestratorViewState = {
  qualificationReport: ReportCTAState;
  requirementSummary: ReportCTAState;
  /**
   * True while an orchestrator run is extracting the bid's details and no title
   * has landed yet. The title streams in asynchronously (via `applyEvent`
   * updating the bid query's name), so consumers can show a "Generating
   * title…" placeholder until `bidName` is populated.
   */
  isGeneratingTitle: boolean;
};

/**
 * Derives the CTA state for one (bidId, reportType) pair. A queued orchestrator
 * for this report type wins over the entry — the user has just clicked
 * Generate but the driver hasn't fired `startBidOrchestrator` yet (e.g. uploads
 * still settling). Otherwise we gate on `entry.generates[reportType]` so a
 * run that didn't ask for this report doesn't light up its CTA.
 */
function deriveReportCTAState(
  bidId: string,
  reportType: BidReportType,
  stream: UseBidOrchestratorStreamResult,
  queuedOrchestrator: QueuedBidOrchestrator | undefined,
): ReportCTAState {
  const {
    entry,
    phase,
    isQualificationReportStreaming,
    isRequirementSummaryStreaming,
    isWinThemeStreaming,
    isQualificationSummaryStreaming,
  } = stream;

  const isQualReport = reportType === BidReportType.Qualification;
  const routeSegment = isQualReport
    ? routeSegments.qualificationReport
    : routeSegments.requirementSummary;

  // Queued orchestrator wins — driver hasn't dispatched yet, but the user has committed.
  if (queuedOrchestrator?.reportType === reportType) {
    return { state: "processing", navigateTo: `/bids/${bidId}/${routeSegment}` };
  }

  if (!entry) return { state: "idle" };
  if (phase === BidOrchestratorPhase.Failed) return { state: "idle" };

  const generates = isQualReport
    ? entry.generates.qualificationReport
    : entry.generates.requirementSummary;
  if (!generates) return { state: "idle" };

  const reportId = isQualReport ? entry.qualificationReportId : entry.requirementSummaryReportId;
  // The qualification report page hosts three sections — treat any of them
  // streaming as the whole page streaming.
  const qualReportPageStreaming =
    isQualificationReportStreaming || isWinThemeStreaming || isQualificationSummaryStreaming;
  const isStreaming = isQualReport ? qualReportPageStreaming : isRequirementSummaryStreaming;
  if (!reportId) {
    return { state: "processing", navigateTo: `/bids/${bidId}/${routeSegment}` };
  }

  return {
    state: isStreaming ? "streaming" : "complete",
    navigateTo: `/bids/${bidId}/${routeSegment}/${reportId}`,
  };
}

/**
 * Single source of truth for the BidContent header's orchestrator-derived UI:
 * the per-report CTA states and the "title still generating" flag. Reads the
 * bid's orchestrator stream and queued enrichments once and derives all three.
 */
export function useBidOrchestratorViewState(
  bidId: string,
  bidName: string,
): BidOrchestratorViewState {
  const stream = useBidOrchestratorStream(bidId);
  const { queuedBidOrchestrators } = useBidOrchestratorStreamContext();
  const queuedOrchestrator = queuedBidOrchestrators[bidId];

  // `phase` covers the pre-streaming extraction phases (the section-based
  // `isStreaming` flags aren't true yet at that point), and the queued
  // orchestrator bridges the gap before the run is tracked.
  const { phase } = stream;
  const isOrchestratorOngoing =
    phase !== BidOrchestratorPhase.Idle &&
    phase !== BidOrchestratorPhase.Success &&
    phase !== BidOrchestratorPhase.Failed;
  const isGeneratingTitle =
    !bidName?.trim() && (isOrchestratorOngoing || queuedOrchestrator !== undefined);

  return {
    qualificationReport: deriveReportCTAState(
      bidId,
      BidReportType.Qualification,
      stream,
      queuedOrchestrator,
    ),
    requirementSummary: deriveReportCTAState(
      bidId,
      BidReportType.RequirementSummary,
      stream,
      queuedOrchestrator,
    ),
    isGeneratingTitle,
  };
}
